import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flame, ArrowLeft, RefreshCw, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const bgImage =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_story-auth/artifacts/v8p1c89b_landing-background.png";

function submitLabel(mode, loading) {
  if (loading) {
    return mode === "signup" ? "Lighting the candle…" : "Signing you in…";
  }
  return mode === "signup" ? "Create my quiet room" : "Log in";
}

export default function Auth() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [pseudonym, setPseudonym] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, login, register } = useAuth();

  const safeNext = (() => {
    const n = params.get("next");
    if (n && n.startsWith("/") && !n.startsWith("//")) return n;
    return "/app/dashboard";
  })();

  useEffect(() => {
    if (user && user !== false && user !== null) {
      navigate(safeNext, { replace: true });
    }
  }, [user, navigate, safeNext]);

  const fetchSuggestion = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/pseudonym-suggestion");
      setPseudonym(data.pseudonym);
    } catch (_err) {
      // Non-critical — user can type their own pseudonym.
    }
  }, []);

  useEffect(() => {
    if (mode === "signup" && !pseudonym) {
      fetchSuggestion();
    }
  }, [mode, pseudonym, fetchSuggestion]);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    const next = new URLSearchParams(params);
    next.set("mode", m);
    setParams(next, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please fill in email and password.");
      return;
    }
    if (mode === "signup") {
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (!pseudonym || pseudonym.length < 3) {
        setError("Please pick a pseudonym.");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        await register(email, password, pseudonym);
        toast.success(`Welcome, ${pseudonym}. Your quiet room is ready.`);
      } else {
        await login(email, password);
        toast.success("Welcome back.");
      }
      navigate(safeNext, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      if (err?.response?.status === 409) {
        // Duplicate email → nudge to login prefilled
        setTimeout(() => {
          const next = new URLSearchParams();
          next.set("mode", "login");
          next.set("email", email);
          setParams(next, { replace: true });
          setMode("login");
        }, 1200);
      }
    } finally {
      setLoading(false);
    }
  };

  const heading = useMemo(
    () => (mode === "signup" ? "Begin your first entry" : "Welcome back to the quiet room"),
    [mode],
  );

  return (
    <div
      className="relative min-h-screen w-full flex items-stretch text-stone-100 bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url('${bgImage}')` }}
    >
      {/* Left: image side */}
      <div className="hidden lg:block relative w-1/2">
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 via-stone-950/20 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-stone-200 hover:text-amber-200 transition-colors"
            data-testid="auth-back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono-mini text-[10px] uppercase tracking-[0.28em]">
              Back to landing
            </span>
          </Link>
          <div className="max-w-md">
            <Flame className="w-6 h-6 text-amber-400 mb-6 candle-flicker" />
            <p className="font-serif-editorial text-3xl leading-snug italic text-stone-100">
              “The words don’t need an audience. Only a page that listens.”
            </p>
            <div className="mt-6 font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/70">
              — Kuch Kahna Hai
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: light dark scrim so mobile form stays readable over the image */}
      <div className="lg:hidden absolute inset-0 bg-stone-950/60" />

      {/* Right: form */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md rise-in">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-6 lg:hidden">
              <Flame className="w-5 h-5 text-amber-400 candle-flicker" />
              <span className="font-serif-editorial text-xl">Kuch Kahna Hai</span>
            </div>
            <h1
              className="font-serif-editorial text-3xl sm:text-4xl text-stone-50 leading-tight"
              data-testid="auth-heading"
            >
              {heading}
            </h1>
            <p className="mt-2 text-stone-400 text-sm">
              {mode === "signup"
                ? "Anonymous. Pseudonymous. Only truth."
                : "Sign in and pick up where the page left off."}
            </p>
          </div>

          {/* Toggle */}
          <div className="inline-flex p-1 rounded-full bg-stone-900/70 border border-stone-800 mb-8">
            <button
              type="button"
              data-testid="auth-toggle-login-btn"
              onClick={() => switchMode("login")}
              className={`px-5 py-2 text-sm rounded-full transition-all ${
                mode === "login"
                  ? "bg-amber-500 text-stone-950 shadow"
                  : "text-stone-300 hover:text-amber-200"
              }`}
            >
              Log in
            </button>
            <button
              type="button"
              data-testid="auth-toggle-signup-btn"
              onClick={() => switchMode("signup")}
              className={`px-5 py-2 text-sm rounded-full transition-all ${
                mode === "signup"
                  ? "bg-amber-500 text-stone-950 shadow"
                  : "text-stone-300 hover:text-amber-200"
              }`}
            >
              Sign up
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label
                  htmlFor="pseudonym"
                  className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/70 block mb-2"
                >
                  Your pseudonym
                </label>
                <div className="flex gap-2">
                  <input
                    id="pseudonym"
                    data-testid="auth-pseudonym-input"
                    value={pseudonym}
                    onChange={(e) => setPseudonym(e.target.value)}
                    placeholder="WhisperingRain_42"
                    className="flex-1 bg-stone-900/70 border border-stone-800 hover:border-stone-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none rounded-xl px-4 py-3 text-stone-100 placeholder:text-stone-600 transition-all"
                  />
                  <button
                    type="button"
                    onClick={fetchSuggestion}
                    data-testid="auth-pseudonym-refresh-btn"
                    className="inline-flex items-center gap-2 px-3 rounded-xl border border-stone-800 text-stone-300 hover:text-amber-300 hover:border-amber-500/50 transition-all"
                    title="Suggest another"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-stone-500">
                  Nobody sees your real name — ever. You can change this later.
                </p>
              </div>
            )}
            <div>
              <label
                htmlFor="email"
                className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/70 block mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-stone-900/70 border border-stone-800 hover:border-stone-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none rounded-xl px-4 py-3 text-stone-100 placeholder:text-stone-600 transition-all"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/70 block mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  data-testid="auth-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full bg-stone-900/70 border border-stone-800 hover:border-stone-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none rounded-xl px-4 py-3 pr-11 text-stone-100 placeholder:text-stone-600 transition-all"
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password-visibility-btn"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-amber-300 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="auth-error-msg"
                className="text-sm text-rose-300 bg-rose-950/40 border border-rose-900/60 rounded-xl px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-stone-950 font-semibold text-base py-3.5 transition-all shadow-[0_0_30px_-10px_rgba(245,158,11,0.6)]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {submitLabel(mode, true)}
                </>
              ) : (
                submitLabel(mode, false)
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-stone-500 font-mono-mini uppercase tracking-[0.24em]">
            {mode === "signup" ? "Already have an account?" : "No account yet?"}{" "}
            <button
              type="button"
              onClick={() => switchMode(mode === "signup" ? "login" : "signup")}
              className="text-amber-300 hover:text-amber-200 underline-offset-4 hover:underline"
              data-testid="auth-mode-switch-inline-btn"
            >
              {mode === "signup" ? "Log in" : "Sign up"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
